import { useMemo, useState } from 'react';
import {
  CARE_AMOUNTS,
  FAMILY_HELP_OPTIONS,
  careAdjustmentsChange,
  careAdjustmentsOf,
  ESTIMATED_CARE,
  isCustomCare,
  type CareAdjustments,
  type CareAmount,
  type CareScenarioChange,
  type FamilyHelp,
} from '@shared/consumerPlan';
import { useCarePlanEditor } from './use-care-plan-editor';
import { useConsumerClient } from './use-consumer-client';

function isSameAdjustments(a: CareAdjustments, b: CareAdjustments): boolean {
  return a.careAmount === b.careAmount && a.familyHelp === b.familyHelp;
}

/**
 * The consumer's optional changes to their care scenario (where care happens, how much care, and how
 * much of it family gives), previewed live against the model and saved only when they continue. Each
 * amount carries the weekly hours it would mean, so the choices show real numbers rather than
 * relative words.
 */
export function useCareAdjustments(clientId: string) {
  const { client, scenario: savedScenario } = useConsumerClient(clientId);
  const { previewScenario, saveScenario } = useCarePlanEditor(client);
  const saved = useMemo(() => (client ? careAdjustmentsOf(client) : ESTIMATED_CARE), [client]);
  const [adjustments, setAdjustments] = useState<CareAdjustments>(saved);
  const [chosenSetting, setChosenSetting] = useState<ConsumerCareSetting | null>(null);
  const careSetting = chosenSetting ?? savedScenario?.careSetting ?? null;

  const settingChange = useMemo<CareScenarioChange>(
    () => (careSetting && careSetting !== savedScenario?.careSetting ? { careSetting } : {}),
    [careSetting, savedScenario?.careSetting],
  );
  const change = useMemo<CareScenarioChange>(
    () => ({
      ...settingChange,
      ...(isSameAdjustments(adjustments, saved) ? {} : careAdjustmentsChange(adjustments)),
    }),
    [settingChange, adjustments, saved],
  );
  const hasChanges = Object.keys(change).length > 0;

  const scenario = useMemo(
    () => (hasChanges ? previewScenario(change) : savedScenario),
    [hasChanges, change, savedScenario, previewScenario],
  );

  const careHoursByAmount = useMemo(
    () =>
      Object.fromEntries(
        CARE_AMOUNTS.map(careAmount => [
          careAmount,
          previewScenario({ ...settingChange, ...careAdjustmentsChange({ ...adjustments, careAmount }) })
            ?.careHoursPerWeek ?? 0,
        ]),
      ) as Record<CareAmount, number>,
    [settingChange, adjustments, previewScenario],
  );

  const familyHoursByHelp = useMemo(
    () =>
      Object.fromEntries(
        FAMILY_HELP_OPTIONS.map(familyHelp => [
          familyHelp,
          previewScenario({ ...settingChange, ...careAdjustmentsChange({ ...adjustments, familyHelp }) })
            ?.familyHoursPerWeek ?? 0,
        ]),
      ) as Record<FamilyHelp, number>,
    [settingChange, adjustments, previewScenario],
  );

  function saveAdjustments() {
    if (hasChanges) saveScenario(change);
  }

  return {
    scenario,
    adjustments,
    careSetting,
    isCustom: isCustomCare(adjustments),
    careHoursByAmount,
    familyHoursByHelp,
    setCareSetting: setChosenSetting,
    setCareAmount: (careAmount: CareAmount) => setAdjustments(current => ({ ...current, careAmount })),
    setFamilyHelp: (familyHelp: FamilyHelp) => setAdjustments(current => ({ ...current, familyHelp })),
    saveAdjustments,
  };
}
